import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Archive } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString("en-US")}`;

const colorFor = (pct) => {
  if (pct >= 100) return "text-emerald-400";
  if (pct >= 75) return "text-amber-400";
  if (pct >= 50) return "text-orange-400";
  return "text-rose-400";
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Sección de "Metas Cerradas" — lista todas las metas cuya fecha_fin ya pasó.
 * Al clickear una card, dispara el callback onSelect(goal) con el objeto de la meta
 * para reusar el mismo dialog de "desglose de reportes" del MetasPanel padre.
 */
export default function MetasCerradasSection({ onSelectGoal }) {
  const [closed, setClosed] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/model-goals`, { withCredentials: true });
        const all = Array.isArray(r.data) ? r.data : [];
        const today = todayIso();
        const list = all
          .filter((g) => (g.fecha_fin || "") < today)
          .sort((a, b) => (b.fecha_fin || "").localeCompare(a.fecha_fin || ""));
        if (mounted) setClosed(list);
      } catch {
        if (mounted) setClosed([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Card
      data-testid="metas-cerradas-section"
      className="bg-[#0a0a0a] border border-zinc-700/40 rounded-2xl"
    >
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-zinc-600 to-zinc-800 flex items-center justify-center">
            <Archive className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-white">Metas Cerradas</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">
              Historial de metas cuyo periodo ya venció · click para ver los reportes que las cumplieron
            </p>
          </div>
          <div className="ml-auto text-sm text-gray-400" data-testid="metas-cerradas-count">
            {closed.length} {closed.length === 1 ? "meta" : "metas"}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-gray-500 text-sm py-4">Cargando…</div>}
        {!loading && closed.length === 0 && (
          <div className="text-center text-gray-500 text-sm py-8">
            Todavía no hay metas cerradas. Cuando una meta pasa su fecha de fin, aparecerá aquí.
          </div>
        )}
        {!loading && closed.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {closed.map((g) => {
              const goalAmt = Number(g.goal_amount) || 0;
              const curr = Number(g.current_amount) || 0;
              const pct = goalAmt > 0 ? Math.min(999, Math.round((curr / goalAmt) * 100)) : 0;
              return (
                <button
                  key={g.id}
                  type="button"
                  data-testid={`meta-cerrada-${g.id}`}
                  onClick={() => onSelectGoal && onSelectGoal(g)}
                  className="text-left bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 hover:border-purple-500/50 hover:bg-zinc-900 transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{g.modelo_name || "—"}</div>
                      <div className="text-xs text-gray-500">
                        {g.period_name || `${g.fecha_inicio} → ${g.fecha_fin}`}
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${colorFor(pct)}`}>{pct}%</div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          pct >= 100
                            ? "bg-emerald-500"
                            : pct >= 75
                            ? "bg-amber-500"
                            : pct >= 50
                            ? "bg-orange-500"
                            : "bg-rose-500"
                        }`}
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[11px] text-gray-400 pt-1">
                      <span>Meta {fmt(goalAmt)}</span>
                      <span className="text-emerald-400 font-semibold">Logrado {fmt(curr)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
